import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, lastValueFrom, map, tap } from 'rxjs';
import { FeatureSet } from '../../models/feature.model';
import {
  PermissionRequest,
  PermissionResponse,
  UserPermissionSet,
  UserPermissionsResponse,
} from '../../models/permission.model';

@Injectable({
  providedIn: 'root',
})
export class UserPermissionsService {
  private http = inject(HttpClient);

  permissionSet = signal<UserPermissionSet>({
    permissions: [],
    features: [],
    businessEntityCodes: [],
  });

  permissions = computed(() => this.permissionSet().permissions);

  permissionTo = (permission: string) => {
    return computed(() => {
      return (
        this.permissions().includes(permission) ||
        this.permissions().includes('*:*')
      );
    });
  };

  userPermissions$ = this.http
    .get<UserPermissionsResponse>('auth/permissions')
    .pipe(
      map((response) => this.getPermissionSetFromResponse(response)),
      tap((permissionSet) => {
        this.permissionSet.set(permissionSet);
      }),
    );

  getPermissionKey(featureSet: FeatureSet, feature: string, entity?: string) {
    return !entity
      ? `${featureSet}:${feature}`
      : `${featureSet}:${feature}:${entity}`;
  }

  hasPermission(
    featureSet?: FeatureSet,
    feature?: string,
    entity?: string,
  ): boolean {
    if (!featureSet || !feature) return true;

    // Build permission key
    const key = this.getPermissionKey(featureSet, feature, entity);

    return (
      this.permissions().includes(key) ||
      this.permissions().includes('*:*') ||
      false
    );
  }

  canView(featureSet?: FeatureSet, entity?: string): boolean {
    return (
      featureSet === 'public' || this.hasPermission(featureSet, 'view', entity)
    );
  }

  // For individual permission checks (uses backend API)
  async checkPermissionAsync(
    featureSet: FeatureSet,
    feature: string,
    entity?: string,
  ): Promise<boolean> {
    try {
      const request: PermissionRequest = {
        entity: entity || 'null',
        resource: featureSet,
        action: feature,
      };

      const response = await firstValueFrom(
        this.http.post<PermissionResponse>('casbin/check', request),
      );

      return response.allowed;
    } catch (error) {
      console.error('Async permission check failed:', error);
      return false;
    }
  }

  getPermissionSetFromResponse(
    response: UserPermissionsResponse,
  ): UserPermissionSet {
    return {
      businessEntityCodes: response.roles, // For ATB only, the assigned roles are business entity codes.
      features: response.entities, // Entities hold the user's available features.
      permissions: response.permissions,
    };
  }

  initialize() {
    const permissionSet = this.permissionSet();
    // We only need to retrieve permissions once per session.
    return permissionSet.permissions.length <= 0
      ? lastValueFrom(this.userPermissions$)
      : Promise.resolve(permissionSet);
  }
}
